// sort-imports-ignore
import React, { forwardRef, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { FieldValues, Path, PathValue, UseFormReturn } from "react-hook-form";

import { Editor } from "@tinymce/tinymce-react";
import { Editor as TinyMCEEditor } from "tinymce";
import "tinymce/tinymce";
import "tinymce/icons/default";
import "tinymce/models/dom";
import "tinymce/plugins/image";
import "tinymce/plugins/link";
import "tinymce/plugins/lists";
import "tinymce/plugins/table";
import "tinymce/skins/ui/oxide/skin.min.css";
import "tinymce/themes/silver";

import { editorImgUpload } from "../lib/api";
import { CONTENT_IMAGE_URL } from "../lib/globals";

interface TinyEditorProps<T extends FieldValues> {
    showSave?: boolean;
    formField?: Path<T>;
    editorValue: string;
    setEditorValue?: React.Dispatch<React.SetStateAction<string>>;
    formMethods?: UseFormReturn<T>;
}

const TinyEditor = <T extends FieldValues>(
    {
        showSave = false,
        formField,
        editorValue,
        setEditorValue,
        formMethods: methods,
    }: TinyEditorProps<T>,
    ref: React.Ref<TinyMCEEditor | null>,
): React.ReactElement => {
    const editorRef = useRef<TinyMCEEditor | null>(null);
    const [dirty, setDirty] = useState(false);

    const save = () => {
        if (editorRef.current) {
            const content = editorRef.current.getContent();
            setDirty(false);
            editorRef.current.setDirty(false);
            console.log(content);
        }
    };

    const handleImageUpload = (blobInfo: any): Promise<string> => {
        return editorImgUpload(blobInfo.blob());
    };

    return (
        <>
            <Editor
                tinymceScriptSrc={"/tinymce/tinymce.min.js"}
                licenseKey="gpl"
                onInit={(_evt, editor) => {
                    editorRef.current = editor;

                    // Register custom email link button
                    editor.ui.registry.addToggleButton("emaillink", {
                        icon: "send",
                        tooltip: "Insert email link",
                        onSetup: function (buttonApi) {
                            const checkMailtoLink = () => {
                                const node = editor.selection.getNode();
                                const anchor = editor.dom.getParent(
                                    node,
                                    "a[href]",
                                ) as HTMLAnchorElement;
                                const href = anchor
                                    ? anchor.getAttribute("href")
                                    : null;
                                const isMailto = !!(
                                    href && href.startsWith("mailto:")
                                );
                                buttonApi.setActive(isMailto);
                            };

                            editor.on("NodeChange", checkMailtoLink);

                            return () =>
                                editor.off("NodeChange", checkMailtoLink);
                        },
                        onAction: function () {
                            // Check if cursor is on an existing mailto link
                            const node = editor.selection.getNode();
                            const anchor = editor.dom.getParent(
                                node,
                                "a[href]",
                            ) as HTMLAnchorElement;
                            const href = anchor
                                ? anchor.getAttribute("href")
                                : null;
                            const isMailto = href && href.startsWith("mailto:");

                            // Extract email and text if editing existing link
                            const existingEmail = isMailto
                                ? href.replace("mailto:", "")
                                : "";
                            const existingText = anchor
                                ? anchor.textContent || ""
                                : "";

                            editor.windowManager.open({
                                title: "Insert Email Link",
                                body: {
                                    type: "panel",
                                    items: [
                                        {
                                            type: "input",
                                            name: "email",
                                            label: "Email address",
                                            placeholder: "user@example.com",
                                        },
                                        {
                                            type: "input",
                                            name: "text",
                                            label: "Link text (optional)",
                                            placeholder:
                                                "Leave blank to use email",
                                        },
                                    ],
                                },
                                buttons: [
                                    {
                                        type: "cancel",
                                        text: "Cancel",
                                    },
                                    {
                                        type: "submit",
                                        text: "Insert",
                                        primary: true,
                                    },
                                ],
                                initialData: {
                                    email: existingEmail,
                                    text:
                                        existingText !== existingEmail
                                            ? existingText
                                            : "",
                                },
                                onSubmit: function (api) {
                                    const data = api.getData();
                                    const email = data.email.trim();
                                    const text = data.text.trim() || email;

                                    if (email) {
                                        if (anchor && isMailto) {
                                            // Update existing link
                                            editor.dom.setAttrib(
                                                anchor,
                                                "href",
                                                `mailto:${email}`,
                                            );
                                            anchor.textContent = text;
                                        } else {
                                            // Insert new link
                                            editor.insertContent(
                                                `<a href="mailto:${email}">${text}</a>`,
                                            );
                                        }
                                    }
                                    api.close();
                                },
                            });
                        },
                    });
                }}
                onDirty={() => setDirty(true)}
                value={editorValue}
                onEditorChange={(newValue, editor) => {
                    setEditorValue && setEditorValue(newValue);
                    if (methods && formField) {
                        methods.setValue(
                            formField,
                            newValue as PathValue<T, any>,
                            {
                                shouldDirty: true,
                            },
                        );
                    }
                }}
                init={{
                    branding: false,
                    height: 500,
                    menubar: false,
                    plugins: ["link", "image", "table", "lists"],
                    toolbar:
                        "undo redo | blocks | bold italic underline | alignleft aligncenter alignright | indent outdent | bullist numlist | table image link emaillink",
                    object_resizing: true,
                    automatic_uploads: true,
                    images_reuse_filename: true,
                    image_description: false,
                    image_prepend_url: CONTENT_IMAGE_URL,
                    images_upload_handler: handleImageUpload,
                    contextmenu: false,
                }}
            />
            {showSave && (
                <>
                    <Button onClick={save} disabled={!dirty}>
                        Save
                    </Button>
                    {dirty && <p>You have unsaved content!</p>}
                </>
            )}
        </>
    );
};

export default forwardRef(TinyEditor) as <T extends FieldValues>(
    props: TinyEditorProps<T> & { ref?: React.Ref<TinyMCEEditor | null> },
) => React.ReactElement;
